import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './modules/auth/guards/auth.guard';
import { NotFoundPageComponent } from './components/not-found-page/not-found-page.component';
import { LcoGuard } from './modules/auth/guards/lco.guard';
import { CqGuard } from './modules/auth/guards/cq.guard';
import { CheckAppGuard } from './modules/auth/guards/check-app.guard';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { SignupComponent } from './modules/auth/components/signup/signup.component';
import { SignupGuard } from './modules/auth/guards/signup.guard';
import { BreachGuard } from './modules/auth/guards/breach.guard';
import { CVRGuard } from './modules/auth/guards/cvr.guard';
import { MaintenanceComponent } from './modules/auth/components/maintenance/maintenance.component';
import { MaintenanceGuard } from './modules/auth/guards/maintenance.guard';
import { SsoErrorComponent } from './components/sso-error/sso-error.component';
import { TermsAndConditionsGuard } from './modules/auth/guards/terms-and-conditions.guard';
import { ResetPasswordComponent } from './modules/auth/components/reset-password/reset-password.component';
import { ForgotPasswordComponent } from './modules/auth/components/forgot-password/forgot-password.component';
import { DiscoveryComponent } from './modules/auth/components/discovery/discovery.component';

const routes: Routes = [
    {
        path: 'sso-error',
        component: SsoErrorComponent,
    },
    {
        path: 'auth',
        component: SsoErrorComponent,
    },
    {
        path: 'terms-and-conditions',
        canActivate: [AuthGuard, CheckAppGuard],
        component: DashboardComponent,
        loadChildren: () => import('./modules/terms-and-conditions/terms-and-conditions.module').then((m) => m.TermsAndConditionsModule),
    },
    {
        path: '',
        canActivate: [AuthGuard, CheckAppGuard, TermsAndConditionsGuard],
        component: DashboardComponent,
        children: [
            {
                path: 'dashboard',
                loadChildren: () => import('./modules/mdr-dashboards/mdr-dashboards.module').then((m) => m.MdrDashboardsModule),
            },
            {
                path: 'support-tickets',
                loadChildren: () => import('./modules/support-tickets/support-tickets.module').then((m) => m.SupportTicketsModule),
            },
            {
                path: 'investigations',
                loadChildren: () => import('./modules/activities/investigations/investigations.module').then((m) => m.InvestigationsModule),
            },
            {
                path: 'alerts-triaged',
                loadChildren: () => import('./modules/activities/alerts-triaged/alerts-triaged.module').then((m) => m.AlertsTriagedModule),
            },
            {
                path: 'customer-verification-requests',
                canActivate: [CVRGuard],
                loadChildren: () =>
                    import('./modules/customer-tasks/customer-verification-requests/customer-verification-requests.module').then(
                        (m) => m.CustomerVerificationRequestsModule,
                    ),
            },
            {
                path: 'recommendations',
                loadChildren: () =>
                    import('./modules/customer-tasks/recommendations/recommendations.module').then((m) => m.RecommendationsModule),
            },
            {
                path: 'threat-hunts',
                loadChildren: () => import('./modules/activities/hunts/hunts.module').then((m) => m.HuntsModule),
            },
            {
                path: 'response-actions',
                loadChildren: () =>
                    import('./modules/activities/response-actions/response-actions.module').then((m) => m.ResponseActionsModule),
            },
            {
                path: 'documents',
                canActivate: [LcoGuard],
                loadChildren: () => import('./modules/documents/documents.module').then((m) => m.DocumentsModule),
            },
            {
                path: 'reports',
                loadChildren: () => import('./modules/reports/reports.module').then((m) => m.ReportsModule),
            },
            {
                path: 'companies',
                loadChildren: () => import('./modules/companies/companies.module').then((m) => m.CompaniesModule),
            },
            {
                path: 'companies-onboarding-issues',
                loadChildren: () =>
                    import('./modules/admin/companies-onboarding-issues/companies-onboarding-issues.module').then(
                        (m) => m.CompaniesOnboardingIssuesModule,
                    ),
            },
            {
                path: 'companies-monitoring-issues',
                loadChildren: () =>
                    import('./modules/admin/companies-monitoring-issues/companies-monitoring-issues.module').then(
                        (m) => m.CompaniesMonitoringIssuesModule,
                    ),
            },
            {
                path: 'companies-offboarding-list',
                loadChildren: () =>
                    import('./modules/admin/companies-offboarding-list/companies-offboarding-list.module').then(
                        (m) => m.CompaniesOffboardingListModule,
                    ),
            },
            {
                path: 'companies-onboarding-history',
                loadChildren: () =>
                    import('./modules/admin/companies-onboarding-history/companies-onboarding-history.module').then(
                        (m) => m.CompaniesOnboardingHistoryModule,
                    ),
            },
            {
                path: 'users',
                loadChildren: () => import('./modules/users/users.module').then((m) => m.UsersModule),
            },
            {
                path: 'emergency-contacts',
                loadChildren: () => import('./modules/emergency-contacts/emergency-contacts.module').then((m) => m.EmergencyContactsModule),
            },
            {
                path: 'pre-approved-actions',
                loadChildren: () =>
                    import('./modules/pre-approved-actions/pre-approved-actions.module').then((m) => m.PreApprovedActionsModule),
            },
            {
                path: 'cq',
                canActivate: [CqGuard],
                loadChildren: () =>
                    import('./modules/customer-questionnaire/customer-questionnaire.module').then((m) => m.CustomerQuestionnaireModule),
            },
            {
                path: 'breach-warranty',
                canActivate: [BreachGuard],
                loadChildren: () => import('./modules/securityaddons/securityaddons.module').then((m) => m.SecurityaddonsModule),
            },
            {
                path: 'notifications',
                canActivate: [CheckAppGuard],
                loadChildren: () => import('./modules/notifications/notifications.module').then((m) => m.NotificationsModule),
            },
            {
                path: '',
                canActivate: [LcoGuard],
                pathMatch: 'prefix',
                loadChildren: () => import('./modules/mdr-dashboards/mdr-dashboards.module').then((m) => m.MdrDashboardsModule),
            },
        ],
    },
    {
        path: 'login',
        pathMatch: 'full',
        redirectTo: '/discovery',
    },
    {
        path: 'discovery',
        component: DiscoveryComponent,
        data: {
            title: 'Discovery',
        },
        canActivate: [SignupGuard],
    },
    {
        path: 'sign-up',
        component: SignupComponent,
        data: {
            title: 'Sign Up',
        },
        canActivate: [SignupGuard],
    },
    // {
    //     path: 'sign-in',
    //     component: SigninComponent,
    //     data: {
    //         title: 'Login',
    //     },
    //     canActivate: [SignupGuard],
    // },
    {
        path: 'reset-password',
        component: ResetPasswordComponent,
        data: {
            title: 'Reset password',
        },
        canActivate: [SignupGuard],
    },
    {
        path: 'forgot-password',
        component: ForgotPasswordComponent,
        data: {
            title: 'Forgot password',
        },
        canActivate: [SignupGuard],
    },
    {
        path: 'maintenance',
        component: MaintenanceComponent,
        data: { title: 'Maintenance' },
        canActivate: [MaintenanceGuard],
    },
    {
        path: '',
        pathMatch: 'full',
        redirectTo: '/discovery',
    },
    {
        path: '**',
        component: NotFoundPageComponent,
        data: {
            title: '404 not found',
        },
    },
];

@NgModule({
    imports: [RouterModule.forRoot(routes, { enableTracing: false })],
    exports: [RouterModule],
})
export class AppRoutingModule {}
